import type { QueryClient } from '@tanstack/vue-query';
import { queryClient } from '@/plugins/vueQuery';
import { queryKeys } from './constants';
import { getTeamBudgetQueryKey } from './queries/budgets';
import { getTeamAthletesListQueryKey } from './queries/athletes';

// Helper function to pass in multiple queries to invalidate
export const queriesToInvalidate = async (queryClient: QueryClient, queryKeys: readonly unknown[][]) => {
  await Promise.all(
    queryKeys.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  );
};

export const invalidateAgreementsByContracts = (teamTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;
      const shouldInvalidateQuery = queryKey.includes(queryKeys.AGREEMENTS_BY_CONTRACT);

      return shouldInvalidateQuery && queryKey.includes(teamTwrn);
    },
  });
};

export const invalidateUserPermissions = (userTwrn?: string | null) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const { queryKey } = query;

      const isUserPermissionsQuery = queryKey.includes(queryKeys.USER_PERMISSIONS);
      const isUserPermissionsListQuery = queryKey.includes(queryKeys.USER_PERMISSIONS_LIST);

      if (!isUserPermissionsQuery && !isUserPermissionsListQuery) {
        return false;
      }

      // If no userTwrn filter, invalidate all matching queries
      if (!userTwrn) {
        return true;
      }

      // The real listing query is ['users', 'getUserPermissionsList'] with no userTwrn.
      // Always invalidate it since it shows all users.
      // Check if this is the listing query without any additional elements (userTwrn)
      const isListingWithoutUserTwrn = isUserPermissionsListQuery
        && queryKey.length === 2
        && queryKey[0] === 'users';

      if (isListingWithoutUserTwrn) {
        return true;
      }

      // For all other queries, only invalidate if the userTwrn matches
      return queryKey.includes(userTwrn);
    },
  });
};

export const invalidateTeamBudgetAthletes = (
  teamTwrn?: string | null,
  budgetTwrn?: string | null,
) => queriesToInvalidate(queryClient, [
  getTeamBudgetQueryKey(teamTwrn, budgetTwrn),
  getTeamAthletesListQueryKey(teamTwrn),
]);

// Query Key Helpers

export const makeMeQueryKey = () => ['users', queryKeys.ME];
export const makeUserPermissionsQueryKey = (userTwrn?: string | null) => userTwrn ? ['users', queryKeys.USER_PERMISSIONS, userTwrn] : ['users', queryKeys.USER_PERMISSIONS];
export const makeUserPermissionsListQueryKey = () => ['users', queryKeys.USER_PERMISSIONS_LIST];

export const makeAthleteEarningsQueryKey = (params: {
  teamTwrn: string | null;
  budgetTwrn: string;
  athleteTwrn: string;
}) => [queryKeys.ATHLETE_EARNINGS, params];

export const makeAthleteNotesQueryKey = (params: {
  teamTwrn: string | null;
  athleteTwrn: string;
}) => [queryKeys.ATHLETE_NOTES, params];

export const makeNotificationSettingsQueryKey = (userTwrn?: string | null) =>
  [queryKeys.NOTIFICATION_SETTINGS, userTwrn];
